import { Instagram, Facebook, Phone, Mail, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  return (
    <footer className="bg-gradient-to-br from-[#F9F0F3] via-[#F7E8EF] to-[#F5E1EC] relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/3 right-1/3 w-[300px] sm:w-[400px] lg:w-[600px] h-[300px] sm:h-[400px] lg:h-[600px] bg-[#FFF5F8] rounded-full mix-blend-multiply filter blur-3xl opacity-50" />
        <div className="absolute bottom-1/3 left-1/3 w-[300px] sm:w-[400px] lg:w-[600px] h-[300px] sm:h-[400px] lg:h-[600px] bg-[#FCE9F1] rounded-full mix-blend-multiply filter blur-3xl opacity-50" />
      </div>

      <div className="container mx-auto px-4 sm:px-6">
        {/* Main Footer Content */}
        <div className="py-12 lg:py-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-16">
            {/* Brand Section */}
            <div className="col-span-1">
              <img 
                src="/images/navbar/logo.png" 
                alt="Cut N Curls" 
                className="h-12 sm:h-16 mb-4 sm:mb-6"
              />
              <p className="text-gray-600 text-sm leading-relaxed mb-6 sm:mb-8">
                Where beauty meets expertise. Our commitment to excellence ensures every client leaves feeling confident and beautiful.
              </p>
              <div className="flex gap-4">
                <a 
                  href="https://www.instagram.com/cutncurlssalonny/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/80 backdrop-blur-sm border border-[#FFB6C1]/20 flex items-center justify-center hover:border-[#FFB6C1]/50 transition-colors"
                >
                  <Instagram className="w-4 h-4 sm:w-5 sm:h-5 text-[#FFB6C1]" />
                </a>
                <a 
                  href="https://www.facebook.com/cutncurlssalon/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/80 backdrop-blur-sm border border-[#FFB6C1]/20 flex items-center justify-center hover:border-[#FFB6C1]/50 transition-colors"
                >
                  <Facebook className="w-4 h-4 sm:w-5 sm:h-5 text-[#FFB6C1]" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className="col-span-1 pt-4 lg:pt-0">
              <h3 className="text-[#FFB6C1] font-medium mb-4 sm:mb-6">Quick Links</h3>
              <ul className="space-y-3 sm:space-y-4">
                {['Home', 'About Us', 'Services', 'Gallery', 'Contact Us'].map((item) => (
                  <li key={item}>
                    <Link 
                      to={item === 'Home' ? '/' : `/${item.toLowerCase().replace(' ', '-')}`}
                      className="text-gray-600 hover:text-[#FFB6C1] transition-colors text-sm sm:text-base"
                    >
                      {item}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div className="col-span-1 pt-4 lg:pt-0">
              <h3 className="text-[#FFB6C1] font-medium mb-4 sm:mb-6">Our Services</h3>
              <ul className="space-y-3 sm:space-y-4">
                {[
                  'Hair Coloring',
                  'Hair Treatment',
                  'Hair Styling',
                  'Hair Extensions',
                  'Keratin Treatment'
                ].map((service) => (
                  <li key={service}>
                    <Link 
                      to="/services"
                      className="text-gray-600 hover:text-[#FFB6C1] transition-colors text-sm sm:text-base"
                    >
                      {service}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div className="col-span-1 pt-4 lg:pt-0">
              <h3 className="text-[#FFB6C1] font-medium mb-4 sm:mb-6">Contact Us</h3>
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <MapPin className="w-5 h-5 text-[#FFB6C1] mt-1" />
                  <p className="text-gray-600 text-sm leading-relaxed">
                    593 Plandome Road,<br />
                    Manhasset, New York 11030
                  </p>
                </div>
                <div className="flex items-center gap-3 sm:gap-4">
                  <Phone className="w-5 h-5 text-[#FFB6C1]" />
                  <a href="tel:+15165446582" className="text-gray-600 hover:text-[#FFB6C1] transition-colors text-sm sm:text-base">
                    (516) 544-6582
                  </a>
                </div>
                <div className="flex items-center gap-3 sm:gap-4">
                  <Mail className="w-5 h-5 text-[#FFB6C1]" />
                  <a href="mailto:cutncurlssalon@gmail.com" className="text-gray-600 hover:text-[#FFB6C1] transition-colors text-sm sm:text-base break-all">
                    cutncurlssalon@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#FFB6C1]/10 py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0">
            <p className="text-gray-600 text-xs sm:text-sm text-center sm:text-left">
              © {new Date().getFullYear()} Cut N Curls. All rights reserved.
            </p>
            <div className="flex gap-4 sm:gap-6">
              <Link to="/privacy-policy" className="text-gray-600 text-xs sm:text-sm hover:text-[#FFB6C1] transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="text-gray-600 text-xs sm:text-sm hover:text-[#FFB6C1] transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;